

import { RefObject, useCallback, useContext, useEffect, useRef, useState } from "react";
import { EventContext } from "@/providers/EventProvider";
import { guestToCreationFields } from "@/lib/utils/guestCreationFields";
import { CreationFieldsRef } from "../../PCreateGuest/PCreateGuest";
import { Actions } from "./components/Actions";
import { AcceptanceActions } from "./components/AcceptanceActions";
import { Editing } from "./components/Editing";
import { UsernameUnchangedDialog } from "./components/UsernameUnchangedDialog";
import {
  buildUpdateGuests,
  hasGuestChanges,
  parseTriStateBoolean,
} from "@/lib/utils/parser";
import { useGuests } from "@/providers/GuestsProvider";
import { CoveringLoadingSpinner } from "../../LoadingSpinner/CoveringLoadingSpinner";

import { useTranslations } from "next-intl";
import { useGuestLogs } from "@/providers/GuestLogs";
import { useGuestCompanions } from "@/providers/GuestCompanions";
import { Guest } from "@/types/Guest";
import { AcceptanceState } from "@/types/enums/AcceptanceState";
import { Field } from "@/types/Field";
import { useFields } from "@/providers/FieldsProvider";
import {
  getMissingRequiredFields,
  RequiredFieldsWarnDialog,
} from "../../PCreateGuest/components/requiredFieldsWarning";
import { WorkflowOverwriteDialog } from "../../PCreateGuest/components/WorkflowOverwriteDialog";
import { useGuestWorkflowPreview } from "@/hooks/useGuestWorkflowPreview";
import { OverwritePreview, GuestTrigger } from "@/lib/utils/workflow-overwrite-preview";
import {
  armGuestReconcile,
  GuestReconciledDetail,
} from "@/lib/guest-reconcile/reconcileScheduler";
import {
  deriveLifecycleTriggers,
  explicitUpdateTrigger,
} from "@/lib/utils/derive-lifecycle-triggers";
import {
  isWorkflowOverwriteWarnHidden,
  setWorkflowOverwriteWarnHidden,
} from "@/lib/guests/workflowOverwriteWarnPref";
import { EmailTrigger } from "@/types/enums/EmailTrigger";
import { useUserLocale } from "@/hooks/useUserLocale";
import {
  describeAdditionalFieldViolation,
  type AdditionalFieldErrorCode,
} from "@/lib/additionalFieldConstraints";
import { describeBatchUpdateError } from "@/lib/i18n/translateBackendError";


type GuestActionsProps = {
  guest: Guest;
  archived: boolean;
  acceptanceState: Guest["acceptanceState"];
  creationFields: CreationFieldsRef;
  creationFieldsDb: RefObject<CreationFieldsRef>;
  setCreationFieldsAction: (creationFields: CreationFieldsRef) => void;
  onCloseAction: () => void;
  avoidUpdate?: boolean;
  // Lifts backend per-field additional-field violations up to GuestDetailBody so
  // GuestInfos can render each one inline under its input (bound-aware message).
  setAdditionalFieldErrorsAction?: (errors: Record<string, string>) => void;
  // PENDING request: the popup is stripped to the decision — no check-in/QR/
  // archive/delete chrome, just Save/Undo + Accept/Decline.
  requestMode?: boolean;
  /**
   * "Does any box show text `creationFields` does not hold?" — asked at the
   * moment Save is clicked (the click blurred the box first, so the answer is
   * current). Today the EMAIL cell is the one that answers yes: it keeps the
   * stored address in the form and the typo in the box, so a Save that only
   * read the form would write the old address back under a red error. See
   * useInvalidFields.
   */
  hasInvalidFields?: () => boolean;
  /** Archive / Unarchive in the header landed — see `Actions.onArchivedChange`. */
  onArchivedChange?: (archived: boolean) => void;
  /**
   * The flag the header's Archive / Unarchive starts from when it mounts: the
   * popup's newest, after an Archive / Unarchive that landed in it (audit:
   * header stale after remount — opening a companion or the quick-add
   * unmounts this header; mounted again on the list's `archived`, it offered
   * "Archive" for a guest just archived). `archived` stays the list's flag for
   * Accept / Decline. Defaults to `archived`.
   */
  headerArchived?: boolean;
};

export const GuestActions: React.FC<GuestActionsProps> = ({
  guest,
  archived,
  creationFields,
  creationFieldsDb,
  acceptanceState,
  setCreationFieldsAction,
  onCloseAction,
  avoidUpdate = false,
  setAdditionalFieldErrorsAction,
  requestMode = false,
  hasInvalidFields,
  onArchivedChange,
  headerArchived,
}) => {
  const guestId = guest.guestId!;
  const [changes, setChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Holds the pending save while the username-unchanged warning is open.
  const [usernameWarning, setUsernameWarning] = useState(false);
  // Holds the empty required fields while the soft required-warning is open.
  const [requiredWarn, setRequiredWarn] = useState<Field[] | null>(null);
  // Holds the workflow overwrite preview while that soft-warning is open.
  const [workflowWarn, setWorkflowWarn] = useState<OverwritePreview | null>(null);
  const { allFields } = useFields();
  // The detail popup sits outside EventProvider and guest rows don't always
  // carry eventId, so the GuestsProvider eventId is the reliable source.
  const eventCtx = useContext(EventContext);
  const { updateGuest, refreshGuests, eventId: guestsEventId } = useGuests();
  const resolvedEventId =
    guest.eventId || guestsEventId || eventCtx?.event?.eventId;
  const { getOverwrites } = useGuestWorkflowPreview(resolvedEventId);
  const { fetchLogs } = useGuestLogs();
  const t = useTranslations("errors");
  const locale = useUserLocale();
  const { allCompanions, setAllCompanionsState } = useGuestCompanions();

  // Latest edited values — lets the post-save workflow reconcile detect that the
  // user resumed editing and bail rather than clobber fresh input.
  const latestFieldsRef = useRef(creationFields);
  // Set when the pre-save preview predicted an automation would change a field,
  // so performSave knows to poll for the async UPDATE_GUEST result.
  const workflowWillChangeRef = useRef(false);
  // The fields the predicted automation targets — the poll watches ONLY these
  // for a server-side change (a whole-object diff is key-order fragile and
  // would false-trigger on the very first poll, before the worker runs).
  const workflowFieldsRef = useRef<{ field: string; isAdditional: boolean }[]>([]);
  // Flipped on unmount so an in-flight poll bails instead of setting state on a
  // closed popup.
  const unmountedRef = useRef(false);
  useEffect(() => () => {
    unmountedRef.current = true;
  }, []);

  useEffect(() => {
    latestFieldsRef.current = creationFields;
    // `hasGuestChanges`, not a stringify of the raw objects: the payload builder
    // normalizes both sides, so a comparison that does not would offer Save for
    // an edit that produces nothing to send — and the save path then settles the
    // form as successful. One source of truth for "is there anything to write".
    setChanges(hasGuestChanges(creationFields, creationFieldsDb.current));
  }, [creationFields]);

  // After a save that an automation will act on, the UPDATE_GUEST runs ASYNC on
  // a server worker — so the changed values aren't in the response. The shared
  // reconcile scheduler re-reads the guest on a widening cadence and broadcasts
  // once a PREDICTED field actually moved; this popup applies the result. The
  // polling itself used to live here, which meant every other surface in the
  // cockpit had none.
  const reconcileBaselineRef = useRef<{
    snapshot: string;
    savedObj: CreationFieldsRef;
  } | null>(null);
  const disarmReconcileRef = useRef<(() => void) | null>(null);

  const armWorkflowReconcile = useCallback(() => {
    const eid = resolvedEventId;
    if (!eid) return;
    const watched = workflowFieldsRef.current;
    if (watched.length === 0) return;
    const savedObj = creationFieldsDb.current;
    reconcileBaselineRef.current = {
      snapshot: JSON.stringify(savedObj),
      savedObj,
    };
    disarmReconcileRef.current?.();
    disarmReconcileRef.current = armGuestReconcile({
      eventId: eid,
      watches: new Map([
        [
          guestId,
          watched.map(({ field, isAdditional }) => ({
            field,
            isAdditional,
            fromRaw: isAdditional
              ? (savedObj.additionalFields as Record<string, unknown> | undefined)?.[field]
              : (savedObj as unknown as Record<string, unknown>)[field],
          })),
        ],
      ]),
    });
  }, [resolvedEventId, guestId, creationFieldsDb]);

  // Disarm on unmount so a landing reconcile can't set state on a closed popup.
  useEffect(
    () => () => {
      disarmReconcileRef.current?.();
      disarmReconcileRef.current = null;
    },
    [],
  );

  useEffect(() => {
    const onReconciled = (evt: Event) => {
      const detail = (evt as CustomEvent<GuestReconciledDetail>).detail;
      if (detail.guestId !== guestId) return;
      const baseline = reconcileBaselineRef.current;
      if (!baseline || unmountedRef.current) return;
      // User resumed editing, or another save moved the baseline → don't
      // clobber fresh input. Also stop watching: the values we predicted are no
      // longer the ones on screen.
      if (
        JSON.stringify(latestFieldsRef.current) !== baseline.snapshot ||
        JSON.stringify(creationFieldsDb.current) !== baseline.snapshot
      ) {
        disarmReconcileRef.current?.();
        disarmReconcileRef.current = null;
        return;
      }
      const fresh = detail.guest;
      const mapped = guestToCreationFields(fresh);
      // The reconcile projection doesn't carry the FE-only mainGuestDTO; keep
      // the existing one so the companion link doesn't blink out.
      mapped.mainGuestDTO = fresh.mainGuestDTO ?? baseline.savedObj.mainGuestDTO;
      creationFieldsDb.current = mapped;
      // Distinct object for state (the form mutates its copy in place on save).
      setCreationFieldsAction({ ...mapped });
      refreshGuests();
      reconcileBaselineRef.current = null;
      // Companions live in a separate provider that refreshGuests doesn't
      // touch — reconcile the freshly-fetched companion there too, else the
      // parent companion list keeps the stale pre-workflow value.
      if (fresh.guestType === "companion") {
        const mainId = fresh.mainGuest || baseline.savedObj.mainGuest || "";
        setAllCompanionsState((prev) => {
          const list = prev.get(mainId);
          if (!list || !list.some((c) => c.guestId === fresh.guestId)) return prev;
          const next = new Map(prev);
          next.set(
            mainId,
            list.map((c) => (c.guestId === fresh.guestId ? fresh : c)),
          );
          return next;
        });
      }
    };
    window.addEventListener("guestReconciled", onReconciled);
    return () => window.removeEventListener("guestReconciled", onReconciled);
  }, [
    guestId,
    creationFieldsDb,
    setCreationFieldsAction,
    refreshGuests,
    setAllCompanionsState,
  ]);

  const performSave = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    setAdditionalFieldErrorsAction?.({});
          try {
            // An undecided request keeps its type. The dropdown is already
            // read-only for these guests (GuestLayoutFieldInput), so this is
            // the second door, not the first — it holds if a custom detail
            // layout or a future surface reaches the field another way, and it
            // is checked BEFORE the mainGuest coercion below, which would
            // otherwise blank a pending companion's parent on the way out.
            // Same string as the backend's error code, so one translation
            // serves both refusals.
            if (
              acceptanceState === AcceptanceState.PENDING &&
              creationFields.guestType !== creationFieldsDb.current.guestType
            ) {
              throw new Error("guestTypeLockedWhilePending");
            }
            if (
              creationFields.guestType === "companion" &&
              !creationFields.mainGuest
            ) {
              throw new Error("companionNeedsMainGuest");
            }
            if (creationFields.guestType === "mainGuest") {
              creationFields.mainGuest = "";
            }

            const newGuest = buildUpdateGuests(
              creationFields,
              creationFieldsDb.current
            );
            // An IMAGE upload is persisted at confirm time (presign→confirm),
            // so a save right after it can diff to NOTHING once the echoed
            // image value is excluded (buildUpdateGuests strips it). Sending a
            // { guestId }-only update would make the backend net zero
            // modifications and fail the guest with `noChanges` — skip the
            // call instead and settle the form as a successful no-op save.
            // Same predicate as the dirty flag above — see hasGuestChanges for
            // why the empty-additionalFields arm exists.
            const hasChanges = hasGuestChanges(
              creationFields,
              creationFieldsDb.current,
            );
            newGuest.guestId = guestId;

            if (hasChanges) {
              await updateGuest(
                newGuest,
                avoidUpdate,
                creationFieldsDb.current.mainGuest,
                creationFields.mainGuest
              );
            }

            // The save committed — settle the form and kick off the workflow
            // poll BEFORE the best-effort logs fetch, so a failing logs call
            // can't leave the form dirty or skip the async-result sync.
            creationFieldsDb.current = creationFields;
            setChanges(false);
            if (workflowWillChangeRef.current) {
              workflowWillChangeRef.current = false;
              armWorkflowReconcile();
            }

            try {
              await fetchLogs(guestId, true);
            } catch {
              // logs are non-critical
            }

            if (
              creationFields.guestType === "companion" &&
              allCompanions.get(creationFields.mainGuest || "")
            ) {
              const newGuest2: Guest = {
                ...newGuest,
                ...creationFields,
                guestType: creationFields.guestType as Guest["guestType"],
                state: creationFields.state as Guest["state"], // Ensure correct type
                gender: creationFields.gender as Guest["gender"], // Cast gender to correct type
                acceptanceState: acceptanceState,
                emailOptIn: parseTriStateBoolean(creationFields.emailOptIn),
                // SMS channel wave: the form's tri-state string back to the guest's boolean.
                smsOptIn: parseTriStateBoolean(creationFields.smsOptIn ?? ""),
              };

              // Update the existing companion in the main guest's companions list
              setAllCompanionsState((prev) => {
                const newMap = new Map(prev);
                const prevCompanions =
                  newMap.get(creationFields.mainGuest || "") || [];

                // Find and update the existing companion, or add if not found
                const companionIndex = prevCompanions.findIndex(
                  (comp) => comp.guestId === newGuest2.guestId
                );

                if (companionIndex !== -1) {
                  // Update existing companion
                  const updatedCompanions = [...prevCompanions];
                  updatedCompanions[companionIndex] = newGuest2;
                  newMap.set(newGuest2.mainGuest || "", updatedCompanions);
                } else {
                  // If companion doesn't exist, add it (fallback behavior)
                  newMap.set(newGuest2.mainGuest || "", [
                    ...prevCompanions,
                    newGuest2,
                  ]);
                }

                return newMap;
              });
            }
          } catch (error) {
            // The backend attaches per-field additional-field violations to the
            // thrown error (see GuestsProvider.updateGuest). Route each to an
            // inline, bound-aware message under its input instead of the generic
            // "some fields failed" banner — the admin sees exactly which field
            // and what the limit is.
            const rich = error as Error & {
              violations?: Array<{ fieldKey: string; code: string }>;
            };
            if (
              rich.violations &&
              rich.violations.length > 0 &&
              setAdditionalFieldErrorsAction
            ) {
              const byName = new Map(
                allFields
                  .filter((f) => f.additionalField)
                  .map((f) => [f.name.toLowerCase(), f]),
              );
              const mapped: Record<string, string> = {};
              for (const v of rich.violations) {
                const f = byName.get(v.fieldKey.toLowerCase());
                if (!f || mapped[f.name]) continue;
                mapped[f.name] = describeAdditionalFieldViolation(
                  f,
                  v.code as AdditionalFieldErrorCode,
                  t,
                  locale,
                );
              }
              const names = Object.keys(mapped);
              if (names.length > 0) {
                setAdditionalFieldErrorsAction(mapped);
                // Name the offending fields in the banner — the custom-fields
                // section can be far below the fold, so listing them tells the
                // admin exactly where to look (inline errors carry the specifics).
                setErrorMessage(
                  t("additionalFieldsValidationFailedList", {
                    fields: names.join(", "),
                  }),
                );
              } else {
                setErrorMessage(describeBatchUpdateError(t, error));
              }
            } else {
              // NOT `t(error.message)`: a batch rejection's envelope code
              // (`batchUpdatePartialFailure`) is not a reason and has no key,
              // so that printed the raw code in red while the actual reason
              // sat unread in `failures[]`.
              setErrorMessage(describeBatchUpdateError(t, error));
            }
          } finally {
            setLoading(false);
          }
  }, [
    creationFields,
    creationFieldsDb,
    guestId,
    avoidUpdate,
    updateGuest,
    fetchLogs,
    allCompanions,
    acceptanceState,
    setAllCompanionsState,
    armWorkflowReconcile,
    allFields,
    locale,
    setAdditionalFieldErrorsAction,
    t,
  ]);

  // Username is a stable login credential — it is NOT derived from the name and
  // stays fixed when first/last name change (changing it would break login
  // links the guest already has). It also surfaces in guest communication: the
  // `{{username}}` email variable and the credentials embedded in every login
  // link. So when the name changed but the username is set and untouched, warn
  // first. This only warns — it never alters the username.
  // Username warn → then save (the second gate after the required warn).
  // Final gate before the write: preview workflow (UPDATE_GUEST) overwrites on
  // GUEST_UPDATED so the admin isn't surprised that an automation changed a
  // field they just edited. Soft — they can save anyway.
  const proceedAfterUsername = useCallback(() => {
    // Every trigger this one save fires, not just the explicit one: the backend
    // ALSO derives lifecycle triggers from the diff and enqueues a message per
    // trigger. Asking only about GUEST_UPDATED meant a status flip here
    // reported "nothing will change" while every GUEST_ACCEPTED pipe fired.
    const triggers = [
      explicitUpdateTrigger(creationFields as unknown as { archived?: boolean | null }),
      ...deriveLifecycleTriggers(
        creationFieldsDb.current as unknown as Record<string, never>,
        creationFields as unknown as Record<string, never>,
      ),
    ].filter((t): t is EmailTrigger => t !== null);
    const preview = getOverwrites(
      creationFields as unknown as Record<string, unknown>,
      creationFieldsDb.current as unknown as Record<string, unknown>,
      triggers as GuestTrigger[],
    );
    // Remember whether an automation will act, so performSave polls for the
    // async result regardless of which gate (dialog or direct) reaches it.
    workflowWillChangeRef.current = preview.entries.length > 0;
    workflowFieldsRef.current = preview.entries.map((e) => ({
      field: e.field,
      isAdditional: e.isAdditional,
    }));
    // Admin opted out of this warning for this event → save straight through.
    if (preview.entries.length > 0 && !isWorkflowOverwriteWarnHidden(resolvedEventId)) {
      setWorkflowWarn(preview);
      return;
    }
    void performSave();
  }, [getOverwrites, creationFields, creationFieldsDb, performSave, resolvedEventId]);

  const runSave = useCallback(() => {
    const db = creationFieldsDb.current;
    const nameChanged =
      creationFields.firstName !== db.firstName ||
      creationFields.lastName !== db.lastName;
    const usernameUntouched = creationFields.username === db.username;
    if (nameChanged && usernameUntouched && (db.username || "").trim()) {
      setUsernameWarning(true);
      return;
    }
    proceedAfterUsername();
  }, [creationFields, creationFieldsDb, proceedAfterUsername]);

  const handleSaveAction = useCallback(() => {
    // A box that shows an address the form does not hold: refuse, and say so
    // next to the buttons. The box itself is already red.
    if (hasInvalidFields?.()) {
      setErrorMessage(t("emailInvalid"));
      return;
    }
    // Soft-warn first if required fields are empty (admin may save anyway).
    const missing = getMissingRequiredFields(creationFields, allFields);
    if (missing.length > 0) {
      setRequiredWarn(missing);
      return;
    }
    runSave();
  }, [creationFields, allFields, runSave, hasInvalidFields, t]);

  return (
    <div className="pb-3 flex flex-row flex-wrap items-start gap-2 justify-start">
      <Editing
        handleSaveAction={handleSaveAction}
        handleUndoAction={() => {
          setCreationFieldsAction(creationFieldsDb.current);
        }}
        changes={changes}
      />

      {!requestMode && (
        <Actions
          archived={headerArchived ?? archived}
          guest={guest}
          onCloseAction={onCloseAction}
          onArchivedChange={onArchivedChange}
        />
      )}
      {/* PENDING request (registrant/companion) → decide it right here, same
          action the requests-table row offers. */}
      <AcceptanceActions
        guest={guest}
        eventId={resolvedEventId}
        archived={archived}
        acceptanceState={acceptanceState}
      />
      {errorMessage && <p className="body-3-regular font-error ml-3">{errorMessage}</p>}
      {loading && <CoveringLoadingSpinner />}
      {usernameWarning && (
        <UsernameUnchangedDialog
          username={creationFieldsDb.current.username || ""}
          onCancel={() => setUsernameWarning(false)}
          onConfirm={() => {
            setUsernameWarning(false);
            proceedAfterUsername();
          }}
        />
      )}
      {workflowWarn && (
        <WorkflowOverwriteDialog
          preview={workflowWarn}
          loading={loading}
          onClose={() => setWorkflowWarn(null)}
          onConfirm={(dontShowAgain) => {
            if (dontShowAgain) setWorkflowOverwriteWarnHidden(resolvedEventId);
            setWorkflowWarn(null);
            void performSave();
          }}
        />
      )}
      {requiredWarn && (
        <RequiredFieldsWarnDialog
          fields={requiredWarn}
          loading={loading}
          onClose={() => setRequiredWarn(null)}
          onConfirm={() => {
            setRequiredWarn(null);
            runSave();
          }}
        />
      )}
    </div>
  );
};
